import { type FormEvent, useRef, useState } from 'react';
import { Alerta, Boton, Campo, ErrorApi, Guia, Pill } from '../components/ui';
import type { ConfiguracionCr, FacturacionCrApi } from './types';

const fecha = (s: string | null) => (s ? new Date(s).toLocaleDateString('es-CR', { day: '2-digit', month: 'short', year: 'numeric' }) : '—');
const SUFIJO = '@prod.comprobanteselectronicos.go.cr';

export function CredencialesCR(p: { api: FacturacionCrApi; config: ConfiguracionCr; onGuardado: (c: ConfiguracionCr) => void }) {
  const { config } = p;
  return (
    <section className="seccion">
      <Guia
        id="cr-credenciales"
        titulo="Cómo cargar su certificado y sus credenciales de Hacienda"
        pasos={[
          <>Ingrese a <b>ATV</b> (Administración Tributaria Virtual) con su usuario de Hacienda.</>,
          <>
            En <b>Comprobantes electrónicos → Generar llave criptográfica</b> descargue el archivo <b>.p12</b> y anote el <b>PIN</b> de 4
            dígitos que usted definió.
          </>,
          <>
            En <b>Comprobantes electrónicos → Generar contraseña</b> obtenga el <b>usuario</b> (termina en <span className="mono">{SUFIJO}</span>) y la
            <b> clave</b> para el envío de comprobantes. Use las de <b>producción</b>, no las de pruebas.
          </>,
          <>Cargue aquí el archivo y el PIN, y luego el usuario y la clave. Al guardar, el sistema comprueba que funcionan.</>,
          <>Cuando el certificado esté por vencer, genere uno nuevo en ATV y cárguelo aquí: no hay que hacer nada más.</>,
        ]}
        nota="El certificado, el PIN y la clave se guardan cifrados y el portal nunca los vuelve a mostrar."
      />
      {!config.emisor && (
        <Alerta tipo="aviso" titulo="Primero registre los datos del emisor">
          El certificado se compara con la identificación del emisor para evitar cargar la llave de otro contribuyente.
        </Alerta>
      )}
      <div className="grid-2 tarjetas-cr">
        <Certificado {...p} />
        <Atv {...p} />
      </div>
    </section>
  );
}

function Certificado({ api, config, onGuardado }: { api: FacturacionCrApi; config: ConfiguracionCr; onGuardado: (c: ConfiguracionCr) => void }) {
  const c = config.certificado;
  const [archivo, setArchivo] = useState<File | null>(null);
  const [pin, setPin] = useState('');
  const [errores, setErrores] = useState<{ archivo?: string; pin?: string }>({});
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [exito, setExito] = useState(false);
  const input = useRef<HTMLInputElement>(null);

  async function guardar(ev: FormEvent) {
    ev.preventDefault();
    const e: typeof errores = {};
    if (!archivo) e.archivo = 'Seleccione el archivo .p12.';
    else if (!/\.(p12|pfx)$/i.test(archivo.name)) e.archivo = 'Debe ser un archivo .p12.';
    else if (archivo.size > 50 * 1024) e.archivo = 'El archivo excede 50 KB.';
    if (!pin.trim()) e.pin = 'Escriba el PIN.';
    setErrores(e);
    if (Object.keys(e).length) return;
    setGuardando(true);
    setError(null);
    setExito(false);
    try {
      onGuardado(await api.guardarCertificado(config.idEmpresa, archivo!, pin.trim()));
      setArchivo(null);
      setPin('');
      if (input.current) input.current.value = '';
      setExito(true);
    } catch (x) {
      setError(x);
    } finally {
      setGuardando(false);
    }
  }

  return (
    <form className="tarjeta-cert" onSubmit={guardar} noValidate>
      <div className="tarjeta-cert__cab">
        <h3>Llave criptográfica (.p12)</h3>
        {c.estado === 'EN_PORTAL' && <Pill tono={c.porVencer ? 'pendiente' : 'ok'}>{c.porVencer ? 'Por vencer' : 'Cargado'}</Pill>}
        {c.estado === 'EN_SERVIDOR' && <Pill tono={c.porVencer ? 'pendiente' : 'neutro'}>En el servidor</Pill>}
        {c.estado === 'VENCIDO' && <Pill tono="error">Vencido</Pill>}
        {c.estado === 'NINGUNO' && <Pill tono="pendiente">Pendiente</Pill>}
      </div>

      {c.estado !== 'NINGUNO' && (
        <dl className="meta">
          <dt>Vence</dt>
          <dd>{fecha(c.vence)}{c.diasParaVencer !== null && c.diasParaVencer >= 0 && <span className="texto-sec"> · en {c.diasParaVencer} días</span>}</dd>
          {c.huella && (<><dt>Huella</dt><dd className="mono quiebre mini">{c.huella.slice(0, 32)}…</dd></>)}
          {c.actualizado && (<><dt>Cargado</dt><dd>{fecha(c.actualizado)}</dd></>)}
        </dl>
      )}
      {c.estado === 'EN_SERVIDOR' && (
        <Alerta tipo="info" titulo="Su certificado ya está configurado.">
          Sigue funcionando. Cárguelo aquí cuando lo renueve para administrarlo usted mismo.
        </Alerta>
      )}
      {c.estado === 'VENCIDO' && <Alerta tipo="error" titulo="El certificado venció. Sus comprobantes no se pueden firmar hasta que cargue uno nuevo." />}

      <Campo etiqueta={c.estado === 'NINGUNO' ? 'Archivo .p12' : 'Reemplazar por un archivo nuevo'} error={errores.archivo}>
        {(id) => (
          <input ref={input} id={id} type="file" accept=".p12,.pfx" className="archivo"
            onChange={(e) => { setArchivo(e.target.files?.[0] ?? null); setErrores((x) => ({ ...x, archivo: undefined })); setExito(false); }} />
        )}
      </Campo>
      <Campo etiqueta="PIN de la llave" error={errores.pin}>
        {(id) => <input id={id} type="password" className="corto" value={pin} maxLength={32} autoComplete="off" inputMode="numeric"
          onChange={(e) => { setPin(e.target.value); setErrores((x) => ({ ...x, pin: undefined })); }} />}
      </Campo>
      <ErrorApi error={error} />
      {exito && <Alerta tipo="exito" titulo="Certificado guardado. Se usará en el próximo envío." />}
      <div className="acciones acciones--inicio">
        <Boton submit cargando={guardando} deshabilitado={!config.emisor}>Guardar certificado</Boton>
      </div>
    </form>
  );
}

function Atv({ api, config, onGuardado }: { api: FacturacionCrApi; config: ConfiguracionCr; onGuardado: (c: ConfiguracionCr) => void }) {
  const c = config.credenciales;
  const [usuario, setUsuario] = useState(c.usuario ?? '');
  const [clave, setClave] = useState('');
  const [errores, setErrores] = useState<{ usuario?: string; clave?: string }>({});
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [exito, setExito] = useState(false);

  async function guardar(ev: FormEvent) {
    ev.preventDefault();
    const u = usuario.trim().toLowerCase();
    const e: typeof errores = {};
    if (u.includes('@stag.')) e.usuario = 'Ese usuario es del ambiente de pruebas. Use el de producción.';
    else if (!u.endsWith(SUFIJO)) e.usuario = `Debe terminar en ${SUFIJO}.`;
    if (!clave) e.clave = 'Escriba la clave.';
    setErrores(e);
    if (Object.keys(e).length) return;
    setGuardando(true);
    setError(null);
    setExito(false);
    try {
      onGuardado(await api.guardarCredenciales(config.idEmpresa, u, clave));
      setClave('');
      setExito(true);
    } catch (x) {
      setError(x);
    } finally {
      setGuardando(false);
    }
  }

  return (
    <form className="tarjeta-cert" onSubmit={guardar} noValidate>
      <div className="tarjeta-cert__cab">
        <h3>Credenciales de Hacienda (ATV)</h3>
        {c.estado === 'VALIDADAS' && <Pill tono="ok">Validadas</Pill>}
        {c.estado === 'EN_SERVIDOR' && <Pill tono="neutro">En el servidor</Pill>}
        {c.estado === 'NINGUNA' && <Pill tono="pendiente">Pendiente</Pill>}
      </div>
      {c.estado === 'VALIDADAS' && c.validadas && <p className="texto-sec">Comprobadas con Hacienda el {fecha(c.validadas)}.</p>}
      {c.estado === 'EN_SERVIDOR' && (
        <Alerta tipo="info" titulo="Sus credenciales ya están configuradas.">
          Siguen funcionando. Si cambia la clave en ATV, escríbala aquí.
        </Alerta>
      )}
      <Campo etiqueta="Usuario" error={errores.usuario}>
        {(id) => <input id={id} className="mono" value={usuario} autoComplete="off" spellCheck={false} placeholder={'cpj-3-101-000000' + SUFIJO}
          onChange={(e) => { setUsuario(e.target.value); setErrores((x) => ({ ...x, usuario: undefined })); setExito(false); }} />}
      </Campo>
      <Campo etiqueta={c.estado === 'NINGUNA' ? 'Clave' : 'Clave nueva'} error={errores.clave} ayuda={c.estado !== 'NINGUNA' ? 'La clave guardada no se muestra.' : undefined}>
        {(id) => <input id={id} type="password" value={clave} autoComplete="new-password"
          onChange={(e) => { setClave(e.target.value); setErrores((x) => ({ ...x, clave: undefined })); }} />}
      </Campo>
      <ErrorApi error={error} />
      {exito && <Alerta tipo="exito" titulo="Hacienda aceptó las credenciales. Quedaron guardadas." />}
      <div className="acciones acciones--inicio">
        <Boton submit cargando={guardando}>{guardando ? 'Comprobando con Hacienda…' : 'Comprobar y guardar'}</Boton>
      </div>
    </form>
  );
}
